import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import {
  useAuth,
  useI18N,
  useCreatePublicVenueNote,
  VenueDetailsBySlugResponse,
  useZoomDetection,
  useModalHeight,
} from "common/hooks";
import { BusinessNoteHeadingSuggestion } from "common/types";

import Loader from "@/components/common/Loader";
import { useCustomToast } from "@/hooks/useCustomToast";
import { XIcon, ChevronDown, Plus } from "lucide-react";
import { InfoIcon } from "@phosphor-icons/react";
import { Textarea } from "@/shadcn/components/ui/textarea";
import WideButton from "@/components/common/WideButton";
import FloatingLabelInput from "@/components/InputBoxes/TextInput";
import { useDarkMode } from "@/hooks/useDarkMode";
import AnalyticsService from "@/services/analytics";
import SelectDropDown from "@/components/common/SelectDropDown";
import RadioButton from "@/components/common/RadioButton";
import { StandardButton } from "@/components/common/StandardButton";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { Calendar } from "@/shadcn/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shadcn/components/ui/popover";
import { DateRange } from "react-day-picker";

// SearchableInput functionality moved inline to simplify component
import { cn } from "@/lib/utils";

function AddVenueNote(props: {
  venueDetailsResponse: VenueDetailsBySlugResponse;
  onClose: () => void;
  onInfoClick: () => void;
}) {
  const i18n = useI18N();
  const { user } = useAuth();
  const { modalStyle, modalClassName } = useModalHeight();
  const showToast = useCustomToast();

  const [notes, setNotes] = useState("");
  const {
    data: mutateResult,
    error,
    status,
    mutate,
  } = useCreatePublicVenueNote();
  const noteRef = useRef(null);
  const isDark = useDarkMode();
  const [applyToWholeVenue, setApplyToWholeVenue] = useState(false);

  const [selectedArea, setSelectedArea] = useState<{
    parentId: number;
    subId: string;
  } | null>(null);
  const [selectedPriority, setSelectedPriority] = useState(0);
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const [title, setTitle] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");
  const [styledPreviewTitle, setStyledPreviewTitle] = useState("");

  // Add state for tracking template suggestion and placeholder values
  const [selectedTemplateSuggestionId, setSelectedTemplateSuggestionId] =
    useState<string | null>(null);
  const [placeholderValues, setPlaceholderValues] = useState<string[]>([]);

  const [isManualAdd, setIsManualAdd] = useState(false);
  const [isManualHeadline, setIsManualHeadline] = useState(false);

  // State to track when suggestions are visible
  const [suggestionsVisible, setSuggestionsVisible] = useState(false);

  // State to track filtered suggestions based on area selection
  const [filteredSuggestions, setFilteredSuggestions] = useState<
    BusinessNoteHeadingSuggestion[]
  >([]);

  // Ref to track when we're switching templates to prevent useEffect interference
  const isSwitchingTemplate = useRef(false);

  // Determine if suggestions dropdown should be enabled
  const isSuggestionsEnabled = useMemo(() => {
    // Enabled if applying to whole venue OR if specific area is selected
    return applyToWholeVenue || selectedArea !== null;
  }, [applyToWholeVenue, selectedArea]);

  // Set of suggestion IDs already used by active Live Alerts (action_id === 9) for this venue
  const existingLiveAlertSuggestionIds = useMemo(() => {
    const existingNotes = props.venueDetailsResponse.details.businessNotesAll || [];
    return new Set(
      existingNotes
        .filter(
          (note) =>
            note.business_notes_action_id === 9 &&
            note.status !== "Inactive" &&
            note.business_note_heading_suggestions_id,
        )
        .map((note) => note.business_note_heading_suggestions_id),
    );
  }, [props.venueDetailsResponse.details.businessNotesAll]);

  // SearchableInput state variables
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<string>("");

  // Close dropdown when suggestions become disabled
  useEffect(() => {
    if (!isSuggestionsEnabled && isDropdownOpen) {
      setIsDropdownOpen(false);
      setSuggestionsVisible(false);
      setSearchTerm("");
      setSelectedIndex(-1);
    }
  }, [isSuggestionsEnabled, isDropdownOpen]);

  // Clear suggestion state when switching between whole venue yes/no
  useEffect(() => {
    // Clear all suggestion-related state when applyToWholeVenue changes
    setSelectedTemplateSuggestionId(null);
    setSelectedTemplate(null);
    setPlaceholderValues([]);
    setTitle("");
    setPreviewTitle("");
    setStyledPreviewTitle("");
    setIsManualAdd(false);
    setIsManualHeadline(false);
    setSelectedPriority(0);

    // Close dropdown if open
    setIsDropdownOpen(false);
    setSuggestionsVisible(false);
    setSearchTerm("");
    setSelectedIndex(-1);
  }, [applyToWholeVenue]);

  // Zoom awareness state and logic
  const isZoomedIn = useZoomDetection();

  // if title is empty, set isManualHeadline to false and selectedPriority to 0
  useEffect(() => {
    if (title.length === 0) {
      setIsManualHeadline(false);
      setSelectedPriority(0);
      setSelectedTemplateSuggestionId(null);
      setPlaceholderValues([]);
      setIsManualAdd(false); // Reset manual mode when title is cleared
    }
  }, [title]);

  // Set isManualHeadline based on whether title matches any suggested heading
  useEffect(() => {
    const matchingSuggestionContainsTitle = filteredSuggestions.find(
      (suggestion) =>
        suggestion.heading.toLowerCase().includes(title.toLowerCase()) ||
        title.toLowerCase().includes(suggestion.heading.toLowerCase()),
    );

    if (matchingSuggestionContainsTitle) {
      setIsManualHeadline(false);
      setSelectedPriority(
        matchingSuggestionContainsTitle.business_notes_action_id,
      );
      // Note: Date range selection is now always available, no need to set hasEndDate
    } else {
      // CheckIcon if the current title matches any of the suggested headings
      const matchingSuggestion = filteredSuggestions.find(
        (suggestion) => suggestion.heading === title,
      );

      // Set isManualHeadline to true if title is not empty and doesn't match any suggestion (manual headline)
      if (title.length > 0 && !matchingSuggestion && selectedPriority === 0) {
        setIsManualHeadline(true);
        setSelectedPriority(10);
        // Don't clear selectedTemplateSuggestionId here - it should persist for the selected suggestion
        // Only clear placeholder values since we're not in template mode
        setPlaceholderValues([]);
        // Note: Date range selection is now always available
      }
    }
  }, [title, filteredSuggestions, isManualHeadline, selectedPriority]);

  const handleSuggestionSelect = useCallback((business_notes_action_id) => {
    console.log(
      "handleSuggestionSelect business_notes_action_id",
      business_notes_action_id,
    );
    setSelectedPriority(business_notes_action_id);

    // If business_notes_action_id is 0, it means manual mode
    if (business_notes_action_id === 0) {
      setIsManualAdd(true);
      // Clear template suggestion ID when switching to manual mode
      console.log("Clearing selectedTemplateSuggestionId for manual mode");
      setSelectedTemplateSuggestionId(null);
    } else {
      // For all other suggestion types, set manual mode to false
      setIsManualAdd(false);
    }

    // Note: Date range selection is now always available, no need to handle hasEndDate logic
  }, []);

  const selectedAreaDetails = useMemo(() => {
    if (selectedArea) {
      const section =
        props.venueDetailsResponse.details.businessVenueNotesAreaLocations.find(
          (e) => e.id.toString() === selectedArea.parentId.toString(),
        );
      const subSection = section?.tags.find(
        (e) => e.id.toString() === selectedArea.subId,
      );
      return {
        parentId: selectedArea.parentId,
        parentName: section?.name,
        subId: selectedArea.subId,
        subName: subSection?.name,
        location_type: section?.location_type,
      };
    }
    return undefined;
  }, [
    selectedArea,
    props.venueDetailsResponse.details.businessVenueNotesAreaLocations,
  ]);

  const selectedPriorityDetails = useMemo(() => {
    return props.venueDetailsResponse.details.businessNotesActionsVenueNotes?.find(
      (action) => action.id === selectedPriority,
    );
  }, [
    selectedPriority,
    props.venueDetailsResponse.details.businessNotesActionsVenueNotes,
  ]);

  const isNoteTextMandatory = useMemo(() => {
    console.log("Debug isNoteTextMandatory:", {
      selectedPriority,
      selectedPriorityDetails,
      isNoteTextMandatory: selectedPriorityDetails?.isNoteTextMandatory,
      result: selectedPriorityDetails?.isNoteTextMandatory === 1,
    });
    return selectedPriorityDetails?.isNoteTextMandatory === 1;
  }, [selectedPriorityDetails, selectedPriority]);

  useEffect(() => {
    if (error) {
      AnalyticsService.trackGAEventHandler("mapperNoteReportError");
      AnalyticsService.trackMx("mapperNoteReportError", {
        VenueId: props.venueDetailsResponse.details.id,
        VenueBrandId: props.venueDetailsResponse.details.brandId,
        VenueBrandName: props.venueDetailsResponse.details.brandName,
        VenueAddress: props.venueDetailsResponse.details.address,
        VenueName: props.venueDetailsResponse.details.name,
        VenueTypes: props.venueDetailsResponse.details.types,
      });

      showToast(error.message, "error");
    }
  }, [error]);

  useEffect(() => {
    if (mutateResult) {
      console.log("mutateResult", mutateResult);
      if (mutateResult.status === "error") {
        showToast(mutateResult.message || i18n.get("errorOccurred"), "error");
        return;
      }
      showToast(i18n.get("noteCreatedSuccessfully"), "success");
      setNotes("");
      setSelectedPriority(0);
      setTitle("");
      setPreviewTitle("");
      setSelectedTemplateSuggestionId(null);
      setPlaceholderValues([]);
      setIsManualAdd(false);
      props.onClose();
    }
  }, [mutateResult]);

  const submit = () => {
    AnalyticsService.trackGAEventHandler("mapperNoteReportCreate");
    AnalyticsService.trackMx("mapperNoteReportCreate", {
      VenueId: props.venueDetailsResponse.details.id,
      VenueBrandId: props.venueDetailsResponse.details.brandId,
      VenueBrandName: props.venueDetailsResponse.details.brandName,
      VenueAddress: props.venueDetailsResponse.details.address,
      VenueName: props.venueDetailsResponse.details.name,
      VenueTypes: props.venueDetailsResponse.details.types,
      ReviewerName: user?.name,
    });

    //Fallback to non-time-sensitive priority if no priority is selected
    if (selectedPriority === 0) {
      setSelectedPriority(10);
    }

    const selectedPriorityDetails =
      props.venueDetailsResponse.details.businessNotesActionsVenueNotes.find(
        (e) => e.id.toString() === selectedPriority.toString(),
      );

    let locationId = props.venueDetailsResponse.details.business.id.toString();
    let locationType = "Business";
    if (!applyToWholeVenue && selectedAreaDetails) {
      console.log("selectedAreaDetails:", selectedAreaDetails);
      locationId =
        selectedAreaDetails?.subId && selectedAreaDetails?.subId !== "0"
          ? selectedAreaDetails?.subId.toString()
          : selectedAreaDetails?.parentId.toString();
      locationType = selectedAreaDetails.location_type || "";
    }

    console.log("locationId:", locationId);
    console.log("locationType:", locationType);

    // Prepare template data
    const business_note_heading_suggestions_id =
      selectedTemplateSuggestionId || "";

    // Log the template suggestion ID for debugging
    console.log(
      "Submitting with business_note_heading_suggestions_id:",
      business_note_heading_suggestions_id,
    );
    console.log(
      "selectedTemplateSuggestionId state:",
      selectedTemplateSuggestionId,
    );

    // If using a template, submit the final preview text with replaced values
    // If not using a template, submit empty placeholder text
    let finalTitle = title;
    let placeholder_text = "";

    if (business_note_heading_suggestions_id) {
      // Using template: submit the final preview text with replaced values
      finalTitle = previewTitle || title;
      placeholder_text = placeholderValues.join("|") || "";
      console.log("Submitting template with final preview text:", finalTitle);
      console.log("Placeholder values:", placeholder_text);
    } else {
      // Not using template: submit empty placeholder text
      placeholder_text = "";
      console.log("Submitting manual headline with empty placeholder text");
    }

    // Validate template data consistency
    if (business_note_heading_suggestions_id && !placeholder_text) {
      console.warn("Template suggestion ID provided but no placeholder text");
    }

    mutate({
      noteId: "",
      businessId:
        props.venueDetailsResponse.details.business.id.toString() || "",
      note: notes,
      title: finalTitle,
      notePriority: selectedPriorityDetails?.id.toString() || "",
      noteAreaType: locationType || "",
      noteAreaId: locationId || "",
      dateFrom: dateRange?.from ? format(dateRange.from, "yyyy-MM-dd") : "",
      dateTo: dateRange?.to ? format(dateRange.to, "yyyy-MM-dd") : "",
      business_note_heading_suggestions_id,
      placeholder_text,
    });
  };

  const isSubmitDisabled = () => {
    // CheckIcon if priority is selected and title has minimum length
    const basicValidation = selectedPriority === 0 || title.length < 5;

    // CheckIcon if note text is mandatory and empty
    const noteTextValidation = isNoteTextMandatory && notes.length < 5;

    // CheckIcon if venue location is properly selected when not applying to whole venue
    const locationValidation = !applyToWholeVenue && selectedArea === null;

    // Note: Date range (note validity) is optional - not included in validation
    // UsersIcon can choose to add it or not without affecting submit button state

    // For other priorities, check basic validation, note text validation, and location validation
    const result = basicValidation || noteTextValidation || locationValidation;

    if (noteTextValidation) {
      console.log("Submit disabled due to mandatory note text:", {
        isNoteTextMandatory,
        notesLength: notes.length,
        notes,
      });
    }

    if (locationValidation) {
      console.log("Submit disabled due to location validation:", {
        applyToWholeVenue,
        selectedArea,
        locationValidation,
      });
    }

    console.log("Submit validation:", {
      selectedPriority,
      titleLength: title.length,
      basicValidation,
      isNoteTextMandatory,
      notesLength: notes.length,
      noteTextValidation,
      locationValidation,
      applyToWholeVenue,
      selectedArea,
      dateRangeFrom: dateRange?.from, // For debugging - not used in validation
      result,
    });

    return result;
  };

  useEffect(() => {
    console.log("attributes", isManualHeadline, selectedPriority);
  }, [isManualHeadline, selectedPriority]);

  // Update previewTitle when title changes (for non-template cases)
  useEffect(() => {
    setPreviewTitle(title);
  }, [title]);

  // Debug placeholder values changes
  useEffect(() => {
    console.log("Placeholder values changed:", placeholderValues);
  }, [placeholderValues]);

  // Debug template suggestion ID changes
  useEffect(() => {
    console.log(
      "selectedTemplateSuggestionId changed to:",
      selectedTemplateSuggestionId,
    );
  }, [selectedTemplateSuggestionId]);

  // Add click outside handler for dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest(".searchable-input-container")) {
        setIsDropdownOpen(false);
        setSuggestionsVisible(false);
        setSearchTerm("");
        setSelectedIndex(-1);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  // SearchableInput event handlers
  const handleInputClick = () => {
    if (isSuggestionsEnabled) {
      setIsDropdownOpen(true);
      setSuggestionsVisible(true);
      setSearchTerm("");
      setSelectedIndex(-1);
    }
  };

  const handleCaretClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isSuggestionsEnabled) return;

    if (isDropdownOpen) {
      setIsDropdownOpen(false);
      setSuggestionsVisible(false);
      setSearchTerm("");
      setSelectedIndex(-1);
    } else {
      setIsDropdownOpen(true);
      setSuggestionsVisible(true);
      setSearchTerm("");
      setSelectedIndex(-1);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!isSuggestionsEnabled) return;

    const newValue = e.target.value;
    setTitle(newValue);

    // If we're in template mode and user starts typing, clear template mode but preserve suggestion ID
    if (selectedTemplate) {
      setSelectedTemplate(null);
      setPlaceholderValues([]);
      // Don't clear selectedTemplateSuggestionId here - preserve it for the selected suggestion
      // This allows users to edit template suggestions while keeping the suggestion ID
    }

    // If user is typing manually (not selecting from suggestions), set manual mode
    if (newValue.length > 0) {
      const exactMatch = filteredSuggestions.find(
        (suggestion) => suggestion.heading === newValue,
      );
      if (exactMatch) {
        // If there's an exact match, this is not manual mode
        setIsManualAdd(false);
        setSelectedTemplateSuggestionId(exactMatch.id.toString());

        // CheckIcon if the exact match is a template
        if (/\[(XIcon|x|Space)\]/.test(exactMatch.heading)) {
          setSelectedTemplate(exactMatch.heading);
          const parts = parseTemplate(exactMatch.heading);
          const placeholdersCount = parts.filter(
            (part) => part.isPlaceholder && !part.isLocationPlaceholder,
          ).length;
          const initialPlaceholderValues = new Array(placeholdersCount).fill(
            "",
          );
          setPlaceholderValues(initialPlaceholderValues);
        } else {
          setSelectedTemplate(null);
          setPlaceholderValues([]);
        }
      } else {
        // If no exact match, this might be manual mode, but preserve suggestion ID if we had one
        setIsManualAdd(true);
        // Don't clear selectedTemplateSuggestionId here - preserve it for the selected suggestion
        setSelectedTemplate(null);
        setPlaceholderValues([]);
      }
    } else {
      // If input is cleared, reset manual mode
      setIsManualAdd(false);
      setSelectedTemplateSuggestionId(null);
      setSelectedTemplate(null);
      setPlaceholderValues([]);
    }

    // Update preview when typing
    setPreviewTitle(newValue);
    setStyledPreviewTitle(newValue);

    // Auto-search when typing
    setSearchTerm(newValue);
    setIsDropdownOpen(true);
    setSuggestionsVisible(true);
    setSelectedIndex(-1);
  };

  const handleSearchableSuggestionSelect = useCallback(
    (suggestion: {
      id: string;
      label: string;
      business_notes_action_id?: number;
    }) => {
      if (suggestion.id === "manual") {
        // Handle manual add option
        setSelectedTemplate(null);
        setPlaceholderValues([]);
        setPreviewTitle(searchTerm);
        setStyledPreviewTitle(searchTerm);
        handleSuggestionSelect(suggestion.business_notes_action_id);
        setSelectedTemplateSuggestionId(null);
        setIsManualAdd(true); // Set manual mode when selecting manual add
      } else {
        // Handle regular suggestion - always set manual mode to false
        setIsManualAdd(false);

        if (/\[(XIcon|x|Space)\]/.test(suggestion.label)) {
          // Template mode: set up template with editable placeholders
          setSelectedTemplate(suggestion.label);
          const parts = parseTemplate(suggestion.label);
          const placeholdersCount = parts.filter(
            (part) => part.isPlaceholder && !part.isLocationPlaceholder,
          ).length;
          const initialPlaceholderValues = new Array(placeholdersCount).fill(
            "",
          );
          setPlaceholderValues(initialPlaceholderValues);

          // If this suggestion contains [Space], update preview
          if (suggestion.label.includes("[Space]")) {
            let previewText = suggestion.label.replace(
              /\[Space\]/g,
              selectedLocation || "[Space]",
            );

            // Replace [XIcon] and [x] placeholders with empty values initially
            const xPlaceholders = parseTemplate(suggestion.label).filter(
              (part) => part.isPlaceholder && !part.isLocationPlaceholder,
            );

            xPlaceholders.forEach((placeholder) => {
              previewText = previewText.replace(placeholder.text, "[XIcon]");
            });

            setPreviewTitle(previewText);
            const styledPreview = generateStyledPreview(
              suggestion.label,
              [],
              selectedLocation || "",
            );
            setStyledPreviewTitle(styledPreview);
          }

          setTitle(suggestion.label);
        } else {
          // Normal mode: set the full text (no placeholders)
          console.log("Setting non-template suggestion:", suggestion.label);

          // Set flag to prevent useEffect interference
          isSwitchingTemplate.current = true;

          // Clear template state first - use direct state setting
          console.log(
            "Clearing template state - selectedTemplate:",
            selectedTemplate,
          );
          setSelectedTemplate(null);
          setPlaceholderValues([]);
          // Don't clear selectedTemplateSuggestionId here - it will be set later for the selected suggestion

          // Then update title and preview
          setTitle(suggestion.label);
          setPreviewTitle(suggestion.label);
          setStyledPreviewTitle(suggestion.label);

          // Reset flag after state updates
          setTimeout(() => {
            isSwitchingTemplate.current = false;
            console.log("Template switching protection removed");

            // Only clear template-related state, NOT the suggestion ID
            console.log(
              "Clearing template state after protection removed (keeping suggestion ID)",
            );
            setSelectedTemplate(null);
            setPlaceholderValues([]);
            // Don't clear selectedTemplateSuggestionId here - it should persist for non-template suggestions
          }, 100); // Increased timeout to ensure state updates complete
        }
        handleSuggestionSelect(suggestion.business_notes_action_id);

        // Always set template suggestion ID for any selected suggestion
        console.log(
          "Setting selectedTemplateSuggestionId to:",
          suggestion.id,
          "for suggestion:",
          suggestion.label,
        );
        setSelectedTemplateSuggestionId(suggestion.id);
      }
      setIsDropdownOpen(false);
      setSuggestionsVisible(false);
      setSearchTerm("");
      setSelectedIndex(-1);
    },
    [searchTerm, selectedTemplate, selectedLocation, handleSuggestionSelect],
  );

  // Add keyboard navigation for dropdown
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isDropdownOpen) return;

      switch (event.key) {
        case "ArrowDown": {
          event.preventDefault();
          const filteredSearchResults = filteredSuggestions.filter(
            (suggestion) =>
              searchTerm
                ? suggestion.heading
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase())
                : true,
          );
          const suggestionsCount =
            filteredSearchResults.length + (searchTerm ? 1 : 0); // +1 for manual add option
          setSelectedIndex((prev) =>
            prev < suggestionsCount ? prev + 1 : prev,
          );
          break;
        }
        case "ArrowUp":
          event.preventDefault();
          setSelectedIndex((prev) => (prev > -1 ? prev - 1 : prev));
          break;
        case "Enter": {
          event.preventDefault();
          const filteredSearchResults = filteredSuggestions.filter(
            (suggestion) =>
              searchTerm
                ? suggestion.heading
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase())
                : true,
          );

          if (
            selectedIndex >= 0 &&
            selectedIndex < filteredSearchResults.length
          ) {
            const suggestion = filteredSearchResults[selectedIndex];
            handleSearchableSuggestionSelect({
              id: suggestion.id.toString(),
              label: suggestion.heading,
              business_notes_action_id: suggestion.business_notes_action_id,
            });
          } else if (
            selectedIndex === filteredSearchResults.length &&
            searchTerm
          ) {
            // Select manual add option
            setIsManualAdd(true);
            handleSuggestionSelect(0);
            setPlaceholderValues([]);
            setSelectedTemplate(null);
            setSuggestionsVisible(false);
            setIsDropdownOpen(false);
            setSearchTerm("");
            setSelectedIndex(-1);
          }
          break;
        }
        case "Escape":
          setIsDropdownOpen(false);
          setSuggestionsVisible(false);
          setSearchTerm("");
          setSelectedIndex(-1);
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [
    isDropdownOpen,
    selectedIndex,
    searchTerm,
    filteredSuggestions,
    handleSearchableSuggestionSelect,
    handleSuggestionSelect,
  ]);

  const handlePlaceholderChange = (index: number, value: string) => {
    const newPlaceholderValues = [...placeholderValues];
    newPlaceholderValues[index] = value;
    setPlaceholderValues(newPlaceholderValues);

    if (selectedTemplate) {
      // Update preview with all placeholders replaced
      let previewText = selectedTemplate;

      // Replace [Space] placeholders with selected location
      previewText = previewText.replace(
        /\[Space\]/g,
        selectedLocation || "[Space]",
      );

      // Replace [XIcon] and [x] placeholders with actual values
      // Get all [XIcon] and [x] placeholders in order
      const xPlaceholders = parseTemplate(selectedTemplate).filter(
        (part) => part.isPlaceholder && !part.isLocationPlaceholder,
      );

      // Replace each [XIcon] placeholder with its corresponding value
      xPlaceholders.forEach((placeholder, placeholderIndex) => {
        const placeholderValue =
          newPlaceholderValues[placeholderIndex] || "[XIcon]";
        previewText = previewText.replace(placeholder.text, placeholderValue);
      });

      setPreviewTitle(previewText);

      // Update styled preview with underlines
      const styledPreview = generateStyledPreview(
        selectedTemplate,
        newPlaceholderValues,
        selectedLocation || "",
      );
      setStyledPreviewTitle(styledPreview);
    }
  };

  const handleAreaSelection = (value: string) => {
    console.log("handleAreaSelection value:", value);
    if (value === "0") {
      setSelectedArea(null);
      return;
    }
    const [parentId, subId] = value.split(".");
    if (parentId && subId) {
      setSelectedArea({
        parentId: Number(parentId),
        subId,
      });
    }
  };

  // Helper function to parse template with [XIcon], [x], and [Space] placeholders
  const parseTemplate = (template: string) => {
    const parts = template.split(/(\[XIcon\]|\[x\]|\[Space\])/);
    return parts.map((part, index) => ({
      text: part,
      isPlaceholder: /^\[(XIcon|x|Space)\]$/.test(part),
      isLocationPlaceholder: /^\[Space\]$/.test(part),
      id: index,
    }));
  };

  // Helper function to generate styled preview with underlines for replaced values
  const generateStyledPreview = (
    template: string,
    placeholderValues: string[],
    selectedLocation: string,
  ): string => {
    let styledPreview = template;

    // Replace [Space] placeholders with selected location (underlined)
    styledPreview = styledPreview.replace(
      /\[Space\]/g,
      `<u>${selectedLocation || "[Space]"}</u>`,
    );

    // Replace [XIcon] and [x] placeholders with actual values (underlined)
    let placeholderIndex = 0;
    styledPreview = styledPreview.replace(/\[(XIcon|x)\]/g, () => {
      const placeholderValue = placeholderValues[placeholderIndex] || "[XIcon]";
      placeholderIndex++;
      return `<u>${placeholderValue}</u>`;
    });

    return styledPreview;
  };

  // Helper function to filter suggestions based on area selection
  const getFilteredSuggestions = useCallback(
    (
      areaValue: string | null,
      allSuggestions: BusinessNoteHeadingSuggestion[],
    ): BusinessNoteHeadingSuggestion[] => {
      // If no area is selected, return all suggestions
      if (!areaValue) {
        return allSuggestions;
      }

      // Parse areaValue to determine filtering mode
      let parentId: string | null = null;

      if (areaValue === "0" || applyToWholeVenue) {
        // Whole Venue mode - look for'0' in area_ids_allowed
        parentId = "0";
      } else {
        // Specific area mode - extract parentId from areaValue
        const parts = areaValue.split(".");
        if (parts.length >= 1) {
          parentId = parts[0];
        }
      }

      if (!parentId) {
        return allSuggestions;
      }

      // Filter suggestions based on area_ids_allowed
      return allSuggestions.filter((suggestion) => {
        const areaIdsAllowed = suggestion.area_ids_allowed;

        // If area_ids_allowed is empty/null, treat as"no restriction" (show for all areas)
        if (!areaIdsAllowed || areaIdsAllowed.trim() === "") {
          return true;
        }

        // Parse CSV and check for exact token match
        const allowedIds = areaIdsAllowed.split(",").map((id) => id.trim());
        return allowedIds.includes(parentId);
      });
    },
    [applyToWholeVenue],
  );

  // Function to get the selected area name for SearchableInput
  const getSelectedAreaName = useCallback(() => {
    if (applyToWholeVenue) {
      return "Whole venue";
    }

    if (!selectedArea) return "";

    const area =
      props.venueDetailsResponse.details.businessVenueNotesAreaLocations.find(
        (area) => area.id.toString() === selectedArea.parentId.toString(),
      );

    if (!area) return "";

    const tag = area.tags.find(
      (tag) => tag.id.toString() === selectedArea.subId,
    );

    if (tag) {
      return `${tag.name}`;
    }

    return area.name;
  }, [
    applyToWholeVenue,
    selectedArea,
    props.venueDetailsResponse.details.businessVenueNotesAreaLocations,
  ]);

  // Update filtered suggestions when area selection changes
  useEffect(() => {
    const areaValue = applyToWholeVenue
      ? "0"
      : selectedArea
        ? `${selectedArea.parentId}.${selectedArea.subId}`
        : null;
    const filtered = getFilteredSuggestions(
      areaValue,
      props.venueDetailsResponse.details.businessNoteHeadingSuggestions,
    );
    setFilteredSuggestions(filtered);

    // Reset preview headline and placeholder text when area changes
    setPreviewTitle("");
    setPlaceholderValues([]);

    // CheckIcon if current selected suggestion is still valid after filtering
    if (selectedTemplateSuggestionId) {
      const isStillValid = filtered.some(
        (suggestion) =>
          suggestion.id.toString() === selectedTemplateSuggestionId,
      );
      if (!isStillValid) {
        // Clear selection if no longer valid
        setSelectedTemplateSuggestionId(null);
        setSelectedTemplate(null);
        setPlaceholderValues([]);
        setTitle("");
      }
    }
  }, [
    selectedArea,
    applyToWholeVenue,
    props.venueDetailsResponse.details.businessNoteHeadingSuggestions,
    getFilteredSuggestions,
    selectedTemplateSuggestionId,
  ]);

  // Update selectedLocation when area changes
  useEffect(() => {
    const areaName = getSelectedAreaName();
    setSelectedLocation(areaName);

    // Also update preview if we have a template
    if (selectedTemplate) {
      let previewText = selectedTemplate;

      // Replace [Space] placeholders with selected location
      previewText = previewText.replace(/\[Space\]/g, areaName || "[Space]");

      // Replace [XIcon] and [x] placeholders with actual values
      // Get all [XIcon] and [x] placeholders in order
      const xPlaceholders = parseTemplate(selectedTemplate).filter(
        (part) => part.isPlaceholder && !part.isLocationPlaceholder,
      );

      // Replace each [XIcon] placeholder with its corresponding value
      xPlaceholders.forEach((placeholder, placeholderIndex) => {
        const placeholderValue = placeholderValues[placeholderIndex] || "[XIcon]";
        previewText = previewText.replace(placeholder.text, placeholderValue);
      });

      setPreviewTitle(previewText);

      // Update styled preview with underlines
      const styledPreview = generateStyledPreview(
        selectedTemplate,
        placeholderValues,
        areaName || "",
      );
      setStyledPreviewTitle(styledPreview);
    } else if (title && !selectedTemplate) {
      // If no template but we have a title, just update the preview with the title
      setPreviewTitle(title);
      setStyledPreviewTitle(title);
    }
  }, [
    selectedArea,
    applyToWholeVenue,
    selectedTemplate,
    placeholderValues,
    title,
    getSelectedAreaName,
  ]);

  return (
    <>
      <section style={modalStyle} className={cn(modalClassName)} role="none">
        {status === "pending" && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex justify-center items-start pt-[250px] z-50">
            <Loader />
          </div>
        )}

        <header
          className={`py-4 min-h-14 flex items-center justify-between text-center px-6 border-b border-border-token-brand-minimal bg-bg-brand-secondary-default ${!isZoomedIn && !suggestionsVisible ? "sticky top-0 z-10" : "relative z-10"}`}
        >
          <StandardButton
            onClick={props.onClose}
            variant="closeModal"
            aria-label="Close modal"
            tabIndex={0}
            icon={<XIcon color={isDark ? "#A39FE8" : "#352FC3"} />}
            className="flex-shrink-0"
          />
          <h1 className="title-s-semibold text-center text-fg-neutral-strong flex-1 px-4">
            {i18n.get("add")}
          </h1>
          <div className="flex-shrink-0 w-10"></div>
          {" "}
          {/* Spacer to balance the layout */}
        </header>

        <div
          aria-label="Modal Content"
          className={`flex-1 min-h-0 ${suggestionsVisible ? "overflow-hidden" : "overflow-y-auto"}`}
          style={{ WebkitOverflowScrolling: "touch" }}
          role="none"
        >
          <div
            className={`self-stretch px-6 pt-2 flex-col justify-start items-center gap-2 flex ${suggestionsVisible ? "h-full mb-20" : ""}`}
          >
            <div className="self-stretch h-30 pt-2 opacity-80 flex-col justify-start items-start gap-1 flex">
              <div className="self-stretch h-12 flex-col justify-start items-start gap-2 flex">
                <div className="self-stretch !text-fg-brand-subtle label-s-medium">
                  {props.venueDetailsResponse.details.name}
                </div>
                <div className="self-stretch text-fg-neutral-strong title-s-semibold">
                  {i18n.get("publicNote")}
                </div>
              </div>
              <div className="self-stretch text-fg-neutral-strong body-l-semibold md:title-s-semibold pt-1">
                {i18n.get("shareYourInsightsToHelp")}
              </div>
            </div>

            <div className="self-stretch rounded-lg outline outline-1 outline-offset-[-1px] outline-[#deddf7] inline-flex flex-col justify-center items-start">
              {/* Apply whole venue */}
              <div className="self-stretch flex flex-col gap-2 p-4">
                <h2 className="title-s-semibold text-fg-neutral-strong">
                  {i18n.get("doesTheNoteApplyToTheWholeVenue")}
                </h2>
                <div className="flex gap-4">
                  <RadioButton
                    checked={applyToWholeVenue}
                    onCheck={(value) => {
                      if (!applyToWholeVenue && value) {
                        setApplyToWholeVenue(true);
                        setSelectedArea(null);
                      }
                    }}
                    label="Yes"
                    name="applyToWholeVenue"
                    value="yes"
                  />
                  <RadioButton
                    checked={!applyToWholeVenue}
                    onCheck={(value) => {
                      if (applyToWholeVenue && value) {
                        setApplyToWholeVenue(false);
                      }
                    }}
                    label="No"
                    name="applyToWholeVenue"
                    value="no"
                  />
                </div>
              </div>

              {/* Area Selection - Only shown when not applying to whole venue */}
              {!applyToWholeVenue && (
                <div className="self-stretch flex flex-col pl-4 pr-4">
                  <SelectDropDown
                    label={i18n.get("noteLocation")}
                    optionsList={[
                      { id: "0", label: i18n.get("selectArea") },
                      ...props.venueDetailsResponse.details.businessVenueNotesAreaLocations
                        .filter((area) => area.id !== "Business")
                        .map((area) => ({
                          id: area.id.toString(),
                          label: area.name,
                          subTitles: area.tags.map((tag) => ({
                            id: tag.id.toString(),
                            label: tag.name,
                          })),
                        })),
                    ]}
                    value={
                      selectedArea
                        ? `${selectedArea.parentId}.${selectedArea.subId}`
                        : "0"
                    }
                    required={true}
                    onChange={(e) => {
                      const value = e.target.value;
                      handleAreaSelection(value);
                    }}
                    optgroup={false}
                  />
                </div>
              )}

              {/* Combined Searchable Note Headline Input */}
              <div className="w-full flex-col justify-center items-start gap-1 flex p-4">
                <div
                  className={cn(
                    "relative w-full searchable-input-container",
                    !isSuggestionsEnabled && "opacity-50 pointer-events-none",
                  )}
                >
                  <div
                    className={cn(
                      "w-full relative",
                      isSuggestionsEnabled && "cursor-pointer",
                    )}
                    role="combobox"
                    aria-expanded={isDropdownOpen}
                    aria-haspopup="listbox"
                    aria-controls="suggestions-dropdown"
                    tabIndex={0}
                    onClick={(e) => {
                      // Only trigger dropdown toggle if clicking on the container, not the input itself
                      const target = e.target as HTMLElement;
                      const isInputElement =
                        target.tagName === "INPUT" || target.closest("input");

                      if (!isInputElement && isSuggestionsEnabled) {
                        handleCaretClick(e);
                      }
                    }}
                    onKeyDown={(e) => {
                      // Allow keyboard users to toggle dropdown when not focused on input
                      const target = e.target as HTMLElement;
                      const isInputElement = target.tagName === "INPUT";

                      if (
                        !isInputElement &&
                        (e.key === "Enter" || e.key === " ") &&
                        isSuggestionsEnabled
                      ) {
                        e.preventDefault();
                        handleCaretClick(e as unknown as React.MouseEvent);
                      }
                    }}
                  >
                    <FloatingLabelInput
                      type="text"
                      label={i18n.get("title")}
                      value={title}
                      required={true}
                      placeholder={
                        isSuggestionsEnabled
                          ? i18n.get("noteHeadlineTemplate")
                          : "Please select an area first"
                      }
                      onChange={handleInputChange}
                      onFocus={handleInputClick}
                      onEnter={() => {
                        if (!isDropdownOpen && isSuggestionsEnabled) {
                          setIsDropdownOpen(true);
                          setSuggestionsVisible(true);
                          setSearchTerm("");
                          setSelectedIndex(-1);
                        }
                      }}
                    />
                    <ChevronDown
                      className={cn(
                        "absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 transition-transform duration-200 pointer-events-none",
                        isSuggestionsEnabled
                          ? "text-fg-neutral-subtle"
                          : "text-fg-neutral-minimal cursor-not-allowed",
                        isDropdownOpen && "rotate-180",
                      )}
                    />
                  </div>

                  {/* Placeholder inputs for template mode */}
                  {selectedTemplate && parseTemplate(selectedTemplate) && (
                    <div className="mt-3 space-y-2">
                      {parseTemplate(selectedTemplate)
                        .filter(
                          (part) =>
                            part.isPlaceholder && !part.isLocationPlaceholder,
                        )
                        .map((part, index) => (
                          <FloatingLabelInput
                            key={`placeholder-row-${index}`}
                            type="text"
                            label={`Placeholder ${part.text}`}
                            value={placeholderValues[index] || ""}
                            onChange={(e) =>
                              handlePlaceholderChange(index, e.target.value)
                            }
                            onFocus={(e) => e.stopPropagation()}
                            onClear={() => handlePlaceholderChange(index, "")}
                            placeholder={i18n.get("addTheMissingDetails")}
                          />
                        ))}
                    </div>
                  )}

                  {/* Suggestions Dropdown */}
                  {isDropdownOpen && isSuggestionsEnabled && (
                    <div
                      id="suggestions-dropdown"
                      role="listbox"
                      className="absolute top-full left-0 right-0 mt-1 bg-bg-neutral-secondary-default border-2 border-primary-500 rounded-[8px] shadow-lg z-50 max-h-80 overflow-hidden"
                    >
                      <div className="max-h-60 overflow-y-auto">
                        {filteredSuggestions.length === 0 ? (
                          <div className="px-3 py-2 label-m-medium text-fg-neutral-subtle">
                            No suggestions for this area
                          </div>
                        ) : (
                          filteredSuggestions
                            .filter((suggestion) =>
                              searchTerm
                                ? suggestion.heading
                                    .toLowerCase()
                                    .includes(searchTerm.toLowerCase())
                                : true,
                            )
                            .map((suggestion, index) => {
                              const isAlreadyAdded =
                                suggestion.business_notes_action_id === 9 &&
                                existingLiveAlertSuggestionIds.has(suggestion.id);
                              return (
                              <div
                                key={suggestion.id}
                                role={isAlreadyAdded ? "presentation" : "button"}
                                tabIndex={isAlreadyAdded ? -1 : 0}
                                aria-disabled={isAlreadyAdded}
                                title={isAlreadyAdded ? "This alert already exists for this venue" : undefined}
                                className={cn(
                                  "px-3 py-2 label-m-medium transition-colors duration-150",
                                  isAlreadyAdded
                                    ? "opacity-40 cursor-not-allowed text-fg-neutral-subtle"
                                    : cn(
                                        "cursor-pointer",
                                        index === selectedIndex
                                          ? "bg-bg-neutral-secondary-default text-fg-neutral-strong"
                                          : "hover:bg-bg-brand-secondary-hover text-fg-neutral-strong",
                                      ),
                                )}
                                onClick={() => {
                                  if (isAlreadyAdded) return;
                                  handleSearchableSuggestionSelect({
                                    id: suggestion.id.toString(),
                                    label: suggestion.heading,
                                    business_notes_action_id:
                                      suggestion.business_notes_action_id,
                                  });
                                }}
                                onKeyDown={(e) => {
                                  if (isAlreadyAdded) return;
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    handleSearchableSuggestionSelect({
                                      id: suggestion.id.toString(),
                                      label: suggestion.heading,
                                      business_notes_action_id:
                                        suggestion.business_notes_action_id,
                                    });
                                  }
                                }}
                              >
                                <div className="flex items-center gap-2">
                                  {suggestion.business_notes_action_id ===
                                    9 && (
                                    <div title="Time sensitive">
                                      <img
                                        src="/images/clock-countdown.svg"
                                        alt="Time sensitive"
                                        className="h-4 w-4 text-fg-error-subtle"
                                      />
                                    </div>
                                  )}
                                  {suggestion.business_notes_action_id ===
                                    10 && (
                                    <div title="Non-Time sensitive">
                                      <InfoIcon className="h-4 w-4 text-fg-neutral-strong" />
                                    </div>
                                  )}
                                  <span>{suggestion.heading}</span>
                                  {isAlreadyAdded && (
                                    <span className="ml-auto label-s-medium text-fg-neutral-subtle italic">
                                      Already added
                                    </span>
                                  )}
                                </div>
                              </div>
                              );
                            })
                        )}

                        {/* Manual add option */}
                        {searchTerm && filteredSuggestions.length > 0 && (
                          <div className="border-t border-border-token-neutral-minimal">
                            <div
                              role="button"
                              tabIndex={0}
                              className={cn(
                                "px-3 py-2 cursor-pointer label-m-medium transition-colors duration-150 flex items-center gap-2",
                                selectedIndex ===
                                  filteredSuggestions.filter((suggestion) =>
                                    searchTerm
                                      ? suggestion.heading
                                          .toLowerCase()
                                          .includes(searchTerm.toLowerCase())
                                      : true,
                                  ).length
                                  ? "bg-bg-neutral-secondary-default text-fg-neutral-strong"
                                  : "hover:bg-container-neutral-minimal text-fg-brand-strong",
                              )}
                              onClick={() => {
                                setIsManualAdd(true);
                                handleSuggestionSelect(0);
                                setPlaceholderValues([]);
                                setSelectedTemplate(null);
                                setSuggestionsVisible(false);
                                setIsDropdownOpen(false);
                                setSearchTerm("");
                                setSelectedIndex(-1);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  setIsManualAdd(true);
                                  handleSuggestionSelect(0);
                                  setPlaceholderValues([]);
                                  setSelectedTemplate(null);
                                  setSuggestionsVisible(false);
                                  setIsDropdownOpen(false);
                                  setSearchTerm("");
                                  setSelectedIndex(-1);
                                }
                              }}
                            >
                              <Plus className="h-4 w-4" />
                              Add"{searchTerm}" manually
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div
                  id="charCountTitle"
                  className="label-s-medium text-fg-neutral-subtle mt-3 mb-1"
                >
                  {!isSuggestionsEnabled ? (
                    <span className="text-fg-neutral-subtle">
                      Please select"Yes" for whole venue or choose a specific
                      area to enable suggestions
                    </span>
                  ) : (
                    <>
                      <span className={title.length < 5 ? "text-red-600" : ""}>
                        {title.length}
                      </span>
                      /50 Characters - at least 5 characters required
                    </>
                  )}
                </div>
              </div>

              <div className="self-stretch h-0 outline outline-1 outline-offset-[-0.50px] outline-[#deddf7]"></div>

              <div className="self-stretch  inline-flex flex-col justify-center items-start p-4">
                {!isManualAdd && (
                  <div className="self-stretch justify-center label-s-medium leading-none">
                    {previewTitle
                      ? i18n.get("previewNoteHeadline")
                      : i18n.get("preview")}
                  </div>
                )}

                {isManualAdd && (
                  <div className="self-stretch inline-flex justify-start items-center gap-2.5">
                    <div className="justify-center label-s-medium leading-none">
                      {i18n.get("preview")}
                    </div>

                    {previewTitle && (
                      <div
                        data-property-1="Blue badge with leading icon"
                        className="pl-3 pr-4 py-1 bg-white rounded-[20px] outline outline-1 outline-offset-[-1px] outline-[#352fc3] flex justify-center items-center gap-2 overflow-hidden"
                      >
                        <div title="Preview">
                          <InfoIcon className="h-4 w-4 text-fg-neutral-strong" />
                        </div>
                        <div className="justify-center label-s-medium leading-none">
                          New note headline
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="inline-flex justify-start items-center gap-2.5">
                  <div className="justify-center">
                    <span
                      className="body-l-semibold leading-tight"
                      dangerouslySetInnerHTML={{
                        __html: styledPreviewTitle
                          ? styledPreviewTitle
                          : previewTitle
                            ? previewTitle
                            : "--",
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="self-stretch h-0 outline outline-1 outline-offset-[-0.50px] outline-[#deddf7] mt-2"></div>

            {/* Note Validity Date Selection - Always Visible */}
            <div className="self-stretch flex flex-col gap-2 p-4">
              <div className="my-2 self-stretch">
                <div className="flex flex-col gap-4">
                  <Popover>
                    <PopoverTrigger asChild>
                      <StandardButton
                        variant="outline"
                        fullWidth={true}
                        className={`justify-between text-left font-normal ${
                          !dateRange?.from &&
                          !dateRange?.to &&
                          "text-muted-foreground"
                        }`}
                      >
                        <span>
                          {dateRange?.from && dateRange?.to ? (
                            <>
                              {format(dateRange.from, "LLL dd, y")} -{" "}
                              {format(dateRange.to, "LLL dd, y")}
                            </>
                          ) : dateRange?.from ? (
                            <>
                              {format(dateRange.from, "LLL dd, y")} - No end
                              date set
                            </>
                          ) : (
                            <span>{i18n.get("noteValidity")}</span>
                          )}
                        </span>
                        <CalendarIcon className="h-4 w-4" />
                      </StandardButton>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-auto p-4 z-[9999] bg-white text-black rounded-md shadow-lg border"
                      align="start"
                      sideOffset={5}
                    >
                      <Calendar
                        mode="range"
                        required={true}
                        selected={dateRange}
                        onSelect={(selected) => {
                          if (
                            !dateRange?.from ||
                            (dateRange.from && dateRange.to)
                          ) {
                            // If no date selected or both dates are selected, start new selection
                            setDateRange({
                              from: selected?.from,
                              to: undefined,
                            });
                          } else {
                            // If we have a from date but no to date, complete the range
                            if (selected?.from && selected?.to) {
                              setDateRange(selected);
                            }
                          }
                        }}
                        initialFocus
                        disabled={(date) =>
                          date < new Date(new Date().setHours(0, 0, 0, 0))
                        }
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>

            <div className="w-full flex-col justify-center items-start gap-1 flex mt-2 mb-4">
              <div className=" w-full" tabIndex={0}>
                <Textarea
                  id="review"
                  placeholder={i18n.get("noteDetails")}
                  className="scrollbar-thin h-28"
                  value={notes}
                  aria-describedby="charCount"
                  onChange={(e) => {
                    setNotes(e.target.value);
                  }}
                  maxLength={150}
                  ref={noteRef}
                  required={false}
                />
                <div
                  id="charCount"
                  className="label-s-medium text-fg-neutral-subtle mt-3"
                >
                  <span
                    className={
                      isNoteTextMandatory && notes.length < 5
                        ? "text-red-600"
                        : ""
                    }
                  >
                    {notes.length}
                  </span>
                  /150 Characters
                  {isNoteTextMandatory
                    ? " - at least 5 characters required"
                    : ""}
                </div>
              </div>
            </div>
          </div>
        </div>

        <footer
          className={`py-3 min-h-14 flex items-center justify-center text-center px-6 border-border-token-brand-minimal bg-bg-brand-secondary-default ${!isZoomedIn && !suggestionsVisible ? "sticky bottom-0 z-10" : ""}`}
        >
          <div className="flex-col justify-end items-center gap-4 flex md:w-full">
            <div className="w-full justify-center items-center inline-flex">
              <WideButton
                disabled={isSubmitDisabled()} // Disable if conditions are not met
                className="grow"
                onClick={() => {
                  submit();
                }}
                aria-label="Submit Mapper note"
              >
                {i18n.get("save")}
              </WideButton>
            </div>
          </div>
        </footer>
      </section>
    </>
  );
}

export default AddVenueNote;
